import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import { blogService } from "../../services/blogService";
import { motion } from "framer-motion";
import { fadeUp, viewport } from "../../lib/motion";

export default function BlogPreview() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    blogService.list().then((list) => {
      /**
       * Featured articles first, then the most recent.
       *
       * This previously took the three newest and ignored `featured`
       * entirely, so marking an article featured did nothing here — the one
       * place an editor would most expect it to count.
       *
       * Array sort is stable, so within each group the API's newest-first
       * order is preserved.
       */
      const ordered = [...list].sort(
        (a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured))
      );
      setPosts(ordered.slice(0, 3));
    });
  }, []);

  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="blog-preview"
    >
      <div className="container-emilio">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Eyebrow>From Our Journal</Eyebrow>
            <RevealText
              as="h2"
              text={"Cleaning advice from\nour professionals."}
              className="mt-4 font-display font-extrabold text-white text-display"
            />
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-emerald font-semibold text-sm hover:gap-3 transition-all"
          >
            All articles <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {posts.map((p) => (
            <motion.article
              key={p.id}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="group"
            >
              <Link
                to={`/blog/${p.slug}`}
                className="block border border-charcoal hover:border-emerald/60 transition-colors"
                data-testid={`blog-preview-${p.slug}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={p.featuredImage}
                    alt={p.imageAltText}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs text-emerald uppercase tracking-[0.18em]">
                    {p.category} · {p.readingTime} min read
                  </div>
                  <h3 className="mt-3 font-display font-bold text-white text-lg leading-tight group-hover:text-emerald transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-mutedtext text-sm">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-white/80 group-hover:text-emerald text-sm font-semibold">
                    Read Article <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
